<template>
  <div class="exception-location-panel">
    <div v-if="!hideHeader" class="d-flex justify-content-between align-items-center mb-2">
      <h6 class="mb-0">
        <i :class="icon + ' me-2'"></i>{{ label }}
        <span class="badge bg-primary ms-2">{{ locations.length }}</span>
      </h6>
      <div class="btn-group btn-group-sm">
        <button 
          class="btn btn-outline-secondary" 
          @click="showPasteModal = true"
          title="Paste from clipboard"
        >
          <i class="bi bi-clipboard"></i>
        </button>
        <button 
          class="btn btn-outline-secondary" 
          @click="showImportModal = true"
          title="Import from CSV"
        >
          <i class="bi bi-upload"></i>
        </button>
        <button 
          v-if="locations.length > 0"
          class="btn btn-outline-danger" 
          @click="confirmClear"
          title="Clear all"
        >
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <!-- Toolbar: Search + Add toggles -->

    <!-- Quick Add Popover -->
    <div v-if="showAddForm" class="add-location-popover mb-2">
      <div class="d-flex align-items-center gap-1 mb-1">
        <strong class="small flex-grow-1">Add Location</strong>
        <button class="btn btn-link btn-sm text-secondary p-0" @click="showAddForm = false" title="Close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="input-group input-group-sm">
        <input 
          type="text" 
          class="form-control" 
          v-model="newLocation"
          :placeholder="placeholder"
          @keyup.enter="addLocation"
          @keyup.escape="showAddForm = false"
          ref="addLocationInput"
        >
        <input 
          type="text" 
          class="form-control" 
          style="max-width: 130px;"
          v-model="newName"
          placeholder="Name (optional)"
          @keyup.enter="addLocation"
          @keyup.escape="showAddForm = false"
        >
        <button class="btn btn-primary" @click="addLocation" :disabled="!newLocation.trim() || isAtLimit">
          <i class="bi bi-plus-lg"></i>
        </button>
      </div>
    </div>

    <!-- Quick Add (shown inline only when no locations exist) -->
    <div v-if="!showAddForm && locations.length === 0" class="input-group input-group-sm mb-2">
      <input 
        type="text" 
        class="form-control" 
        v-model="newLocation"
        :placeholder="placeholder"
        @keyup.enter="addLocation"
        ref="addLocationInput"
      >
      <input 
        type="text" 
        class="form-control" 
        style="max-width: 130px;"
        v-model="newName"
        placeholder="Name (optional)"
        @keyup.enter="addLocation"
      >
      <button class="btn btn-primary" @click="addLocation" :disabled="!newLocation.trim() || isAtLimit">
        <i class="bi bi-plus-lg"></i>
      </button>
    </div>

    <!-- Location limit warning -->
    <div v-if="isAtLimit" class="alert alert-warning py-1 px-2 mb-2 small d-flex align-items-center">
      <i class="bi bi-exclamation-triangle me-2"></i>
      Location limit reached ({{ maxLocations }}). Remove locations to add more.
    </div>

    <!-- Location List -->
    <div v-if="locations.length === 0" class="text-muted text-center py-4 border rounded bg-light">
      <i class="bi bi-geo-alt fs-3 d-block mb-2"></i>
      <p class="mb-1">No {{ itemLabel }}s added yet.</p>
      <small>Enter a location above or paste/import multiple locations.</small>
    </div>

    <div v-else class="location-list" :style="{ maxHeight: maxHeight }">
      <div 
        v-for="(loc, index) in filteredLocations" 
        :key="loc._origIndex"
        class="location-item d-flex align-items-center gap-2 p-2 border-bottom"
        :class="{ 'bg-light': index % 2 === 0 }"
      >
        <!-- Editing mode -->
        <template v-if="editingIndex === loc._origIndex">
          <div class="flex-grow-1 d-flex gap-1">
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="editLocation"
              placeholder="Location"
              @keyup.enter="saveEdit(loc._origIndex)"
              @keyup.escape="cancelEdit"
              ref="editLocationInput"
            >
            <input
              type="text"
              class="form-control form-control-sm"
              style="max-width: 120px;"
              v-model="editName"
              placeholder="Name"
              @keyup.enter="saveEdit(loc._origIndex)"
              @keyup.escape="cancelEdit"
            >
          </div>
          <button class="btn btn-link btn-sm text-success p-0" @click="saveEdit(loc._origIndex)" title="Save">
            <i class="bi bi-check-lg"></i>
          </button>
          <button class="btn btn-link btn-sm text-secondary p-0" @click="cancelEdit" title="Cancel">
            <i class="bi bi-x-lg"></i>
          </button>
        </template>
        <!-- Display mode -->
        <template v-else>
          <div class="flex-grow-1 location-display" @click="startEdit(loc._origIndex)" role="button" title="Click to edit">
            <div class="fw-medium small">{{ loc.name || loc.location }}</div>
            <div v-if="loc.name" class="text-muted small text-truncate" style="max-width: 250px;">
              {{ loc.location }}
            </div>
          </div>
          <button 
            class="btn btn-link btn-sm text-danger p-0" 
            @click="removeLocation(loc._origIndex)"
            title="Remove"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </template>
      </div>
    </div>

    <!-- Search (below location list) -->
    <div v-if="locations.length > 5" class="mt-2">
      <div class="input-group input-group-sm">
        <span class="input-group-text bg-transparent border-end-0"><i class="bi bi-search text-muted"></i></span>
        <input
          type="text"
          class="form-control border-start-0"
          v-model="searchQuery"
          placeholder="Filter locations..."
          ref="searchInput"
        >
        <button v-if="searchQuery" class="btn btn-outline-secondary" @click="searchQuery = ''" title="Clear">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Import Wizard Modal (Paste or File) -->
    <Teleport to="#vcpopovercontainer" :disabled="!hasTeleportTarget">
      <div v-if="importMode" class="modal fade show" style="display: block; position: fixed; top: 0; left: 0; z-index: 1055; width: 100%; height: 100%; overflow-x: hidden; overflow-y: auto;" tabindex="-1">
        <div class="modal-backdrop fade show" style="position: fixed; top: 0; left: 0; z-index: 1050; width: 100vw; height: 100vh; background-color: rgba(0,0,0,0.5);" @click="closeImportWizard"></div>
        <div class="modal-dialog modal-dialog-centered" :class="{ 'modal-lg': importStep === 'map' }" style="position: relative; z-index: 1060;">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="bi me-2" :class="importMode === 'paste' ? 'bi-clipboard' : 'bi-upload'"></i>
                {{ importMode === 'paste' ? 'Paste Locations' : 'Import from CSV' }}
              </h5>
              <button type="button" class="btn-close" @click="closeImportWizard"></button>
            </div>
            <div class="modal-body">
              <!-- Step 1: Input -->
              <template v-if="importStep === 'input'">
                <!-- Paste mode -->
                <template v-if="importMode === 'paste'">
                  <p class="small text-muted mb-2">
                    Paste locations from a spreadsheet or text. Quoted CSV values (e.g. <code>"New York, NY"</code>) are supported.
                  </p>
                  <textarea
                    class="form-control"
                    rows="8"
                    v-model="pasteContent"
                    placeholder='New York, NY&#10;Los Angeles, CA&#10;Chicago, IL&#10;&#10;Or with names:&#10;HQ,"New York, NY"&#10;Branch,"Los Angeles, CA"'
                  ></textarea>
                </template>
                <!-- File mode -->
                <template v-else>
                  <p class="small text-muted mb-2">
                    Select a CSV or text file. Column mapping is done in the next step.
                  </p>
                  <input
                    type="file"
                    class="form-control"
                    accept=".csv,.txt,.tsv"
                    @change="handleFileSelect"
                    ref="fileInput"
                  >
                </template>
              </template>

              <!-- Step 2: Column Mapping -->
              <template v-else-if="importStep === 'map'">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="form-check form-switch mb-0">
                    <input class="form-check-input" type="checkbox" v-model="hasHeaderRow" id="importHeaderToggle" @change="autoDetectColumnRoles">
                    <label class="form-check-label small" for="importHeaderToggle">First row contains headers</label>
                  </div>
                  <small class="text-muted">{{ mappedLocations.length }} location{{ mappedLocations.length !== 1 ? 's' : '' }}</small>
                </div>
                <p class="small text-muted mb-2">
                  Assign each column as <strong>Address</strong>, <strong>Name</strong>, or <strong>Skip</strong>.
                  Multiple address columns are joined automatically (e.g. Street + City + State).
                </p>
                <div class="table-responsive border rounded" style="max-height: 260px;">
                  <table class="table table-sm table-bordered mb-0" style="font-size: 0.8rem;">
                    <thead class="table-light sticky-top">
                      <tr>
                        <th v-for="ci in columnCount" :key="ci" class="p-1" style="min-width: 110px;">
                          <select class="form-select form-select-sm py-0" v-model="columnRoles[ci - 1]" style="font-size: 0.75rem;">
                            <option value="address">Address</option>
                            <option value="name">Name</option>
                            <option value="skip">Skip</option>
                          </select>
                          <div v-if="hasHeaderRow && parsedRows.length > 0" class="text-muted text-truncate mt-1" style="font-size: 0.7rem;">
                            {{ parsedRows[0][ci - 1] || '' }}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, ri) in previewDataRows" :key="ri">
                        <td v-for="ci in columnCount" :key="ci" class="p-1 text-truncate" style="max-width: 180px;" :title="row[ci - 1] || ''">
                          {{ row[ci - 1] || '' }}
                        </td>
                      </tr>
                      <tr v-if="importDataRows.length > 5">
                        <td :colspan="columnCount" class="text-center text-muted p-1" style="font-size: 0.75rem;">
                          &hellip; {{ importDataRows.length - 5 }} more rows
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="mappedPreview.length > 0" class="mt-3">
                  <small class="fw-medium">Result preview:</small>
                  <div class="border rounded p-2 mt-1 bg-light" style="max-height: 100px; overflow-y: auto;">
                    <div v-for="(loc, i) in mappedPreview" :key="i" class="small text-truncate">
                      <i class="bi bi-geo-alt text-primary me-1"></i>
                      <span v-if="loc.name" class="fw-medium">{{ loc.name }}: </span>{{ loc.location }}
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <div class="modal-footer">
              <template v-if="importStep === 'input'">
                <button class="btn btn-secondary btn-sm" @click="closeImportWizard">Cancel</button>
                <button v-if="importMode === 'paste'" class="btn btn-primary btn-sm" @click="advanceToMapping(pasteContent)" :disabled="!pasteContent.trim()">
                  <i class="bi bi-arrow-right me-1"></i>Next
                </button>
              </template>
              <template v-else>
                <button class="btn btn-outline-secondary btn-sm" @click="importStep = 'input'">
                  <i class="bi bi-arrow-left me-1"></i>Back
                </button>
                <button class="btn btn-primary btn-sm" @click="finalizeImport" :disabled="mappedLocations.length === 0">
                  <i class="bi bi-plus-lg me-1"></i>Add {{ mappedLocations.length }} Location{{ mappedLocations.length !== 1 ? 's' : '' }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Styled Confirmation Dialog -->
    <Teleport to="#vcpopovercontainer" :disabled="!hasTeleportTarget">
      <div v-if="showClearConfirm" class="modal fade show" style="display: block; position: fixed; top: 0; left: 0; z-index: 1075; width: 100%; height: 100%;" tabindex="-1">
        <div class="modal-backdrop fade show" style="position: fixed; top: 0; left: 0; z-index: 1070; width: 100vw; height: 100vh; background-color: rgba(0,0,0,0.5);" @click="showClearConfirm = false"></div>
        <div class="modal-dialog modal-sm modal-dialog-centered" style="position: relative; z-index: 1080;">
          <div class="modal-content">
            <div class="modal-header py-2">
              <h6 class="modal-title"><i class="bi bi-exclamation-triangle text-warning me-2"></i>Confirm</h6>
              <button class="btn-close" @click="showClearConfirm = false"></button>
            </div>
            <div class="modal-body">
              <p class="mb-1">Remove all {{ locations.length }} {{ itemLabel }}s?</p>
              <small class="text-muted">This action cannot be undone.</small>
            </div>
            <div class="modal-footer py-2">
              <button class="btn btn-outline-primary btn-sm" @click="showClearConfirm = false">Cancel</button>
              <button class="btn btn-danger btn-sm" @click="executeClear">
                <i class="bi bi-trash me-1"></i>Remove All
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'ExceptionLocationPanel',

  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: 'Locations'
    },
    itemLabel: {
      type: String,
      default: 'location'
    },
    icon: {
      type: String,
      default: 'bi bi-geo-alt'
    },
    placeholder: {
      type: String,
      default: 'Enter address, city, or coordinates...'
    },
    maxHeight: {
      type: String,
      default: '300px'
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    maxLocations: {
      type: Number,
      default: 0
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      newLocation: '',
      newName: '',
      showPasteModal: false,
      showImportModal: false,
      pasteContent: '',
      // Import wizard state
      importMode: null,
      importStep: 'input',
      parsedRows: [],
      hasHeaderRow: false,
      columnRoles: [],
      editingIndex: -1,
      editLocation: '',
      editName: '',
      showClearConfirm: false,
      searchQuery: '',
      showAddForm: false
    };
  },

  computed: {
    locations: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    /** Whether the location limit has been reached */
    isAtLimit() {
      return this.maxLocations > 0 && this.locations.length >= this.maxLocations;
    },

    /** Check if #vcpopovercontainer exists for Teleport target */
    hasTeleportTarget() {
      return typeof document !== 'undefined' && !!document.getElementById('vcpopovercontainer');
    },

    /** Locations filtered by search query, with original index preserved */
    filteredLocations() {
      const locs = this.locations.map((loc, i) => ({ ...loc, _origIndex: i }));
      if (!this.searchQuery.trim()) return locs;
      const q = this.searchQuery.toLowerCase();
      return locs.filter(loc =>
        (loc.location || '').toLowerCase().includes(q) ||
        (loc.name || '').toLowerCase().includes(q)
      );
    },

    /** Number of columns detected in parsed data */
    columnCount() {
      if (!this.parsedRows.length) return 0;
      return Math.max(...this.parsedRows.map(r => r.length));
    },

    /** Data rows excluding header row if applicable */
    importDataRows() {
      if (!this.parsedRows.length) return [];
      return this.hasHeaderRow ? this.parsedRows.slice(1) : this.parsedRows;
    },

    /** First 5 data rows for the preview table */
    previewDataRows() {
      return this.importDataRows.slice(0, 5);
    },

    /** Locations mapped from column assignments */
    mappedLocations() {
      const count = this.columnCount;
      return this.importDataRows.map(row => {
        const addressParts = [];
        let name = null;
        for (let i = 0; i < count; i++) {
          const val = (row[i] || '').trim();
          if (!val) continue;
          const role = this.columnRoles[i] || 'skip';
          if (role === 'address') addressParts.push(val);
          else if (role === 'name' && !name) name = val;
        }
        return { location: addressParts.join(', '), name: name || null };
      }).filter(loc => loc.location);
    },

    /** First 5 mapped locations for preview */
    mappedPreview() {
      return this.mappedLocations.slice(0, 5);
    }
  },

  watch: {
    showPasteModal(val) {
      if (val) {
        this.showPasteModal = false;
        this.openImportWizard('paste');
      }
    },
    showImportModal(val) {
      if (val) {
        this.showImportModal = false;
        this.openImportWizard('file');
      }
    }
  },

  methods: {
    addLocation() {
      const location = this.newLocation.trim();
      if (!location) return;

      if (this.isAtLimit) return;

      const newLocs = [...this.locations, {
        location,
        name: this.newName.trim() || null
      }];
      this.$emit('update:modelValue', newLocs);

      this.newLocation = '';
      this.newName = '';
    },

    removeLocation(index) {
      const newLocs = [...this.locations];
      newLocs.splice(index, 1);
      this.$emit('update:modelValue', newLocs);
    },

    confirmClear() {
      this.showClearConfirm = true;
    },

    executeClear() {
      this.$emit('update:modelValue', []);
      this.showClearConfirm = false;
    },

    startEdit(index) {
      this.editingIndex = index;
      this.editLocation = this.locations[index].location;
      this.editName = this.locations[index].name || '';
      this.$nextTick(() => {
        const inputs = this.$refs.editLocationInput;
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[0] : inputs;
          input?.focus();
        }
      });
    },

    saveEdit(index) {
      const location = this.editLocation.trim();
      if (!location) return;
      const newLocs = [...this.locations];
      newLocs[index] = {
        location,
        name: this.editName.trim() || null
      };
      this.$emit('update:modelValue', newLocs);
      this.editingIndex = -1;
    },

    cancelEdit() {
      this.editingIndex = -1;
    },

    /** Parse a single CSV line handling quoted fields (RFC 4180) */
    parseCSVLine(line, delimiter) {
      const cells = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"') {
            if (i + 1 < line.length && line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            current += ch;
          }
        } else if (ch === '"') {
          inQuotes = true;
        } else if (ch === delimiter) {
          cells.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      cells.push(current);
      return cells;
    },

    /** Detect whether content is tab or comma delimited */
    detectDelimiter(text) {
      const sample = text.split('\n').slice(0, 5).join('\n');
      let tabs = 0, commas = 0, inQuotes = false;
      for (const ch of sample) {
        if (ch === '"') inQuotes = !inQuotes;
        else if (!inQuotes && ch === '\t') tabs++;
        else if (!inQuotes && ch === ',') commas++;
      }
      return tabs >= commas && tabs > 0 ? '\t' : ',';
    },

    /** Parse CSV/TSV content into rows of string arrays */
    parseCSVContent(text) {
      const delimiter = this.detectDelimiter(text);
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const rows = lines.map(line => this.parseCSVLine(line, delimiter));
      const maxCols = Math.max(...rows.map(r => r.length));
      return rows.map(r => {
        while (r.length < maxCols) r.push('');
        return r;
      });
    },

    /** Check if a row looks like a header */
    isLikelyHeader(row) {
      const headerWords = /^(name|location|address|street|city|state|zip|postal|country|lat|lon|latitude|longitude|site|store|office|branch|id|code|region|area|county)$/i;
      return row.some(cell => headerWords.test(cell.trim()));
    },

    /** Auto-detect column roles from headers or content */
    autoDetectColumnRoles() {
      const count = this.columnCount;
      if (!count) return;
      const roles = new Array(count).fill('address');
      if (this.hasHeaderRow && this.parsedRows.length > 0) {
        const headers = this.parsedRows[0];
        const namePattern = /^(name|site|store|office|branch|label|title|nick)$/i;
        const skipPattern = /^(id|code|notes?|comments?|phone|email|fax|contact|url|website)$/i;
        let hasName = false;
        for (let i = 0; i < count; i++) {
          const h = (headers[i] || '').trim();
          if (!hasName && namePattern.test(h)) {
            roles[i] = 'name';
            hasName = true;
          } else if (skipPattern.test(h)) {
            roles[i] = 'skip';
          }
        }
      }
      this.columnRoles = roles;
    },

    /** Open the import wizard in paste or file mode */
    openImportWizard(mode) {
      this.importMode = mode;
      this.importStep = 'input';
      this.pasteContent = '';
      this.parsedRows = [];
      this.hasHeaderRow = false;
      this.columnRoles = [];
    },

    /** Close the import wizard and reset state */
    closeImportWizard() {
      this.importMode = null;
      this.importStep = 'input';
      this.parsedRows = [];
      this.hasHeaderRow = false;
      this.columnRoles = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    /** Parse content and advance to column mapping step */
    advanceToMapping(content) {
      this.parsedRows = this.parseCSVContent(content);
      if (!this.parsedRows.length) return;
      this.hasHeaderRow = this.isLikelyHeader(this.parsedRows[0]);
      this.autoDetectColumnRoles();
      this.importStep = 'map';
    },

    /** Handle file selection — parse and advance to mapping */
    handleFileSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        if (typeof content === 'string') {
          this.advanceToMapping(content);
        }
      };
      reader.readAsText(file);
    },

    /** Finalize import: add mapped locations and close */
    finalizeImport() {
      if (this.mappedLocations.length > 0) {
        let toAdd = this.mappedLocations;
        if (this.maxLocations > 0) {
          const remaining = this.maxLocations - this.locations.length;
          if (remaining <= 0) {
            this.closeImportWizard();
            return;
          }
          toAdd = toAdd.slice(0, remaining);
        }
        this.$emit('update:modelValue', [...this.locations, ...toAdd]);
      }
      this.closeImportWizard();
    }
  }
};
</script>

<style scoped>
.location-list {
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.location-item:last-child {
  border-bottom: none !important;
}

.location-display {
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
  transition: background 0.15s;
}

.location-display:hover {
  background: #e9ecef;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  position: relative;
  z-index: 1060;
}

.add-location-popover {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 8px 10px;
}
</style>
